// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the three modes.
//
// A mode has to change how the game PLAYS, not just a number, and in a game made entirely of
// information there is exactly one axis worth turning: WHAT A HINT MEANS, and WHAT A DISCARD COSTS.
// So none of these three touch the spark or haze counts at all.
//
//   Vigil   five colours                    the baseline. Hints are unambiguous; the game is tempo —
//                                           can you tell people enough, fast enough, to keep raising.
//   Prism   a sixth suit touched by EVERY    the meaning of a hint changes. "Cyan" no longer means
//           colour hint                      cyan, it means cyan-or-Prism, and a card pointed at as
//                                           two different colours is provably Prism. Deduction, not
//                                           tempo, becomes the bottleneck.
//   Onyx    a sixth suit with ONE copy of    the verb inverts. In Vigil most cards are spare and a
//           every rank                       discard is free tempo; here six of the deck's cards can
//                                            never be replaced, so hints stop being about what to
//                                            raise and start being about what NOT to lose.
//
// The HOST's pick travels frozen inside rematch.ts's round start. `modeOf` guards an id off the wire
// with Object.hasOwn — a bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined
// fields, which reaches the deck builder as `undefined` and deals a game nobody can play.

import { ONYX, PRISM, maxScore, suitsFor, type SuitSpec } from './cards';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** The sixth suit, or null for the plain five. */
  sixth: SuitSpec | null;
}

export const MODES: Record<string, Mode> = {
  vigil: {
    id: 'vigil',
    name: 'Vigil',
    blurb:
      'Five colours, and a hint means exactly what it says. The straight game — learn it here.',
    sixth: null,
  },
  prism: {
    id: 'prism',
    name: 'Prism',
    blurb:
      'A sixth suit that every colour hint touches. Point at Cyan and you may be pointing at a Prism — but a card pointed at twice, in two colours, can only be one thing.',
    sixth: PRISM,
  },
  onyx: {
    id: 'onyx',
    name: 'Onyx',
    blurb:
      'A sixth suit holding one single copy of each rank. Nothing in it can ever be replaced, so a hint stops being about what to raise and starts being about what not to lose.',
    sixth: ONYX,
  },
};

export const MODE_IDS: string[] = ['vigil', 'prism', 'onyx'];
export const DEFAULT_MODE = 'vigil';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

export const suitsOf = (mode: Mode): SuitSpec[] => suitsFor(mode.sixth);
export const perfectScore = (mode: Mode): number => maxScore(suitsOf(mode));
